/**
 * 시딩 모집형 상세 — service DTO → presentation `SeedingCampaignDetail` 매핑.
 *
 * service 의 `screenState`(11종) 를 화면 `status`(9종) 로 환원하고, 나머지 표시 필드는
 * `seedingViewCommon` 헬퍼로 파생한다. BE 미제공 copy/필드는 안전 fallback + NOTE.
 */

import type {
  GetSeedingRecruitmentDetailServiceResponse,
  SeedingRecruitmentScreenState,
} from '@musinsa/services-curator';
import type {
  SeedingCampaignDetail,
  SeedingCampaignInfo,
  SeedingContentTypeData,
  SeedingPrimaryCta,
  SeedingRecruitStatus,
} from '../types';
import {
  applyPenaltyToCta,
  buildBrand,
  buildContentGuide,
  buildContentSubmission,
  buildEditableContentType,
  buildFrozenContentType,
  buildPenalty,
  buildShippingAddress,
  buildShippingTracking,
  contentTypeLabel,
  formatRecruitmentPeriod,
  formatRecruitShippingPlanned,
  formatShippingDate,
  mapGoodsToProducts,
  parseKstDateTime,
  snsChannelLabel,
} from './seedingViewCommon';

// service 의 screenState(런타임 상수) 대신 문자열 리터럴 키 사용 — Record<Union> 가 누락을
// 컴파일 타임에 보장한다. (services 패키지 런타임 import 회피 → jest 환경에서 device-detection JSX
// 미변환 문제 없음.)
const STATUS_BY_SCREEN_STATE: Record<SeedingRecruitmentScreenState, SeedingRecruitStatus> = {
  BEFORE_APPLY: 'recruiting',
  APPLIED: 'applied',
  AWAITING_SELECTION: 'closedSelecting',
  SELECTED_PREPARING: 'selected',
  SHIPPING: 'shipping',
  DELIVERED: 'delivered',
  CONTENT_SUBMITTED: 'contentRegistered',
  NOT_SELECTED: 'rejected',
  // NOTE: 전용 화면 미정의 — 종료 상태는 rejected(결과 안내) 로 fallback.
  SELF_WITHDRAWN: 'rejected',
  CONTENT_EXPIRED: 'rejected',
  // 미참여(또는 자진취소)+전(全) 품절 — 모집 기간 내엔 progress 로 보내지 않고 신청 화면을 그대로
  // 노출하되 신청 CTA 만 비활성화한다. 'recruiting' 으로 환원해 신청 화면 레이아웃을 재사용하고,
  // 전품절 판정은 어댑터가 goods 로 재도출해 CTA disabled + 안내문구를 건다. (미참여+전품절, 캠페인 254)
  SOLD_OUT_UNAVAILABLE: 'recruiting',
};

/**
 * 신청 단계(전체 캠페인 정보 카드 5개 행 노출) — recruiting / applied / closedSelecting.
 * closedSelecting(모집종료+선정중)은 info card 게이트(visibility)엔 포함됐으나 본 set 누락으로
 * 5개 행(선정인원/모집기간/선정예정일/SNS채널/콘텐츠유형)이 비어 미노출됐다. (SID-4313)
 */
const APPLY_STAGE_STATUSES = new Set<SeedingRecruitStatus>([
  'recruiting',
  'applied',
  'closedSelecting',
]);
/**
 * 배송지 변경 가능 단계 — 선정 완료(주문 생성) 전까지. (SID-4116)
 * recruiting(모집중) / applied(신청완료) / closedSelecting(모집종료+선정중). selected 이후 불가.
 */
const EDITABLE_ADDRESS_STATUSES = new Set<SeedingRecruitStatus>([
  'recruiting',
  'applied',
  'closedSelecting',
]);
/** 발송 예정일 행 노출 단계 — 상품준비중(selected)/배송중(shipping) 만. (SID-4327) */
const SHIPPING_PLANNED_STATUSES = new Set<SeedingRecruitStatus>(['selected', 'shipping']);
/** 신청 번호 노출 단계. */
const APPLICATION_NUMBER_STATUSES = new Set<SeedingRecruitStatus>([
  'selected',
  'shipping',
  'delivered',
  'contentRegistered',
]);

const buildPrimaryCta = (
  status: SeedingRecruitStatus,
  {
    // 자진취소 후 재진입(모집기간 내라 recruiting 화면으로 복귀)이면 신청하기를 비활성으로 강제.
    // 취소한 캠페인은 재신청 불가 — 화면은 노출하되 CTA 만 막는다. (SID-4366, SID-4182 대응1 정책 반전)
    applyDisabled = false,
    // 전(全) 품절은 "신청 불가 안내 후 빠져나가기" 의미라 disabled-APPLY 가 아니라 제안형과 동일한
    // '확인'/GO_BACK CTA 로 분리한다. (SID-4166) disabled-APPLY 와 묶으면 마감/동의 게이트와 겹침.
    allSoldOut = false,
  }: { applyDisabled?: boolean; allSoldOut?: boolean } = {},
): SeedingPrimaryCta | null => {
  switch (status) {
    case 'recruiting':
      if (allSoldOut) {
        return {
          mode: 'single',
          variant: 'blur',
          // 제안형 soldOut 과 동일 — '확인' 클릭 시 직전 페이지 복귀(router.back). (SID-4166)
          primary: { label: '확인', action: 'GO_BACK', disabled: false },
        };
      }
      return {
        mode: 'single',
        primary: { label: '신청하기', action: 'APPLY', disabled: applyDisabled },
      };
    case 'applied':
      return {
        mode: 'single',
        primary: { label: '신청 내역 보기', action: 'VIEW_APPLICATION', disabled: false },
      };
    case 'closedSelecting':
      return {
        mode: 'single',
        primary: { label: '신청 취소', action: 'CANCEL_APPLICATION', disabled: false },
      };
    default:
      return null;
  }
};

const buildCountdown = (
  status: SeedingRecruitStatus,
  data: GetSeedingRecruitmentDetailServiceResponse,
): SeedingCampaignDetail['countdown'] => {
  const { campaign, participation } = data;
  // 신청 완료(applied) / 모집 종료·선정 중(closedSelecting) 모두 선정 발표(announcementAt)까지
  // 카운트다운. applied 에 countdown 이 없어 '선정까지 남은 시간'이 안 뜨던 문제. (SID-4212)
  if (status === 'applied' || status === 'closedSelecting') {
    return { label: '선정까지 남은 시간', targetAt: parseKstDateTime(campaign.announcementAt) };
  }
  // 콘텐츠 등록 완료(제출완료) — 카운트다운 숨기고 완료 텍스트만 H1 자리에 노출. (SID-4178)
  if (status === 'contentRegistered') {
    return { label: '콘텐츠 등록 완료', targetAt: null };
  }
  if (status === 'delivered') {
    const deadline = participation?.contentSubmissionDeadlineAt;
    return {
      label: '콘텐츠 등록까지 남은 시간',
      targetAt: deadline ? parseKstDateTime(deadline) : null,
      // 기한 경과 시 카운트다운 숨기고 경과 텍스트만 노출. (SID-4178 작업2, Figma 2194-107052)
      expiredLabel: '콘텐츠 제출 기한 경과',
    };
  }
  return null;
};

const buildContentType = (
  status: SeedingRecruitStatus,
  data: GetSeedingRecruitmentDetailServiceResponse,
): SeedingContentTypeData => {
  const { campaign, participation } = data;
  const selectedContentType = participation?.selectedContentType ?? null;
  if (status === 'recruiting' || selectedContentType === null) {
    return buildEditableContentType(campaign, null, participation?.messageToBrand ?? '');
  }
  return buildFrozenContentType(
    campaign,
    selectedContentType,
    participation?.messageToBrand ?? '',
    // 배송완료(delivered) 이후에만 실제 마감일("YYYY-MM-DD까지 업로드"), 그 전(준비중/배송중)엔 정책 문구. (SID-4319)
    status === 'delivered' || status === 'contentRegistered'
      ? (participation?.contentSubmissionDeadlineAt ?? null)
      : null,
  );
};

const buildCampaignInfo = (
  status: SeedingRecruitStatus,
  data: GetSeedingRecruitmentDetailServiceResponse,
): SeedingCampaignInfo => {
  const {
    campaign,
    participation,
    fulfillments,
    estimatedShippingDeadlineAt,
    shippingDeadlineDays,
  } = data;

  if (APPLY_STAGE_STATUSES.has(status)) {
    const contentTypeLabels = campaign.contentTypes.map((option) => contentTypeLabel(option.type));
    const selectedTypeLabel = participation?.selectedContentType
      ? contentTypeLabel(participation.selectedContentType)
      : '';
    return {
      // 신청 전(recruiting)은 선택 가능한 전체 콘텐츠 유형(다중이면 '중 택 1'), 신청 후(applied)는
      // 선택한 유형만 안내. (노드 211 / 제안형·모집형 공통 원칙)
      title:
        status === 'recruiting'
          ? `콘텐츠 유형 : ${contentTypeLabels.join(', ')}${contentTypeLabels.length > 1 ? ' 중 택 1' : ''}`
          : `콘텐츠 유형 : ${selectedTypeLabel}`,
      description: '제공된 모든 상품을 착용/연출한 콘텐츠를 업로드해야 합니다.',
      // 신청전~신청완료 노출 순서: 선정 인원 → 모집 기간 → 선정 예정일 → SNS 채널 → 콘텐츠 유형. (SID-4327, Figma)
      infoRows: [
        { label: '선정 인원', value: `${campaign.maxParticipants}명` },
        {
          label: '모집 기간',
          value: formatRecruitmentPeriod(campaign.recruitmentStartAt, campaign.recruitmentEndAt),
        },
        { label: '선정 예정일', value: formatShippingDate(campaign.announcementAt, 'short') },
        { label: 'SNS 채널', value: campaign.snsChannels.map(snsChannelLabel).join(', ') },
        {
          label: '콘텐츠 유형',
          value: campaign.contentTypes.map((option) => contentTypeLabel(option.type)).join(', '),
        },
      ],
    };
  }

  if (status === 'rejected') {
    return {
      title: '캠페인 정보',
      description: '이번 캠페인 결과가 안내되었습니다.',
      infoRows: [],
    };
  }

  const selectedTypeLabel = participation?.selectedContentType
    ? contentTypeLabel(participation.selectedContentType)
    : '';
  // 발송 시작 후(fulfillments 존재)엔 확정값 우선, 그 전엔 BE 추정값. (API 0615)
  const shippingPlannedDate =
    fulfillments[0]?.shippingDeadlineAt ?? estimatedShippingDeadlineAt ?? null;
  const infoRows = SHIPPING_PLANNED_STATUSES.has(status)
    ? [
        {
          label: '발송 예정일',
          // "MM.DD (요일) / 선정일로부터 영업일 N일 이내". N 은 BE shippingDeadlineDays.
          value: formatRecruitShippingPlanned(shippingPlannedDate, shippingDeadlineDays),
        },
      ]
    : [];

  return {
    title: `콘텐츠 유형 : ${selectedTypeLabel}`,
    description: '제공된 모든 상품을 착용/연출한 콘텐츠를 업로드해야 합니다.',
    infoRows,
  };
};

export const mapRecruitmentDetailToView = (
  data: GetSeedingRecruitmentDetailServiceResponse,
): SeedingCampaignDetail => {
  const { campaign, goods, participation, fulfillments, contentSubmissions, deliveryAddress } =
    data;
  const status = STATUS_BY_SCREEN_STATE[data.screenState];
  const penalty = buildPenalty(data.penalty);

  // 신청 완료(applied)는 카테고리 미노출 — 신청 전(recruiting)/신청 내역(closedSelecting)만. (SID-4326 수정3-2)
  const showCategory = status === 'recruiting' || status === 'closedSelecting';
  const isCompleted = participation?.status === 'COMPLETED';
  // 자진취소 후 모집기간 내 재진입은 recruiting 화면으로 복귀하되 재신청 불가 → 신청하기 비활성. (SID-4366)
  const isWithdrawnReentry = participation?.status === 'SELF_WITHDRAWN';

  const products = mapGoodsToProducts(goods, {
    showCategory,
    actionVerb: status === 'recruiting' ? '선택' : null,
  });
  // 부분 품절(일부만 soldOut) — 신청 전(recruiting)·신청 후(applied)·선정중(closedSelecting) 전 단계에서
  // '일부 상품만 제공' 양해 안내(적색)를 노출한다. 신청 이후 미노출되던 결함 정정. (SID-4317)
  // 전(全) 품절은 아래 allSoldOut 분기에서 별도 안내(신청 불가)로 처리한다.
  const hasPartialSoldOut =
    products.some((product) => product.soldOut) && products.some((product) => !product.soldOut);
  const showPartialSoldOutGuide = APPLY_STAGE_STATUSES.has(status) && hasPartialSoldOut;
  const PARTIAL_SOLD_OUT_GUIDE =
    '브랜드사 사정으로 인한 상품 품절로, 부득이하게 일부 상품만 제공해 드립니다. 너른 양해 부탁드립니다.';
  // 신청 화면(recruiting)에서 전(全) 상품 품절이면 신청 불가 — CTA 비활성 + 안내(적색). (미참여+전품절 254)
  const allSoldOut =
    status === 'recruiting' && products.length > 0 && products.every((product) => product.soldOut);
  const ALL_SOLD_OUT_GUIDE = '브랜드사 사정으로 상품이 모두 품절되어 캠페인을 신청할 수 없습니다.';

  return {
    campaignId: campaign.id,
    participationId: participation?.id ?? null,
    kind: 'recruit',
    status,
    // 모집 마감 시각(KST ms) — 만료 캠페인 직접 진입 시 신청 제출 차단 가드용. (SID-4180 연계)
    recruitmentEndAtMs: parseKstDateTime(campaign.recruitmentEndAt),
    campaignName: campaign.title,
    brand: buildBrand(campaign),
    countdown: buildCountdown(status, data),
    campaignInfo: buildCampaignInfo(status, data),
    applicationInfo: null,
    products,
    // SID-4317 — 부분 품절(신청·상품준비중)은 '제외하고 진행'이 아니라 '일부 상품만 제공' 안내(적색). Figma 1356-127459.
    productSectionGuide: allSoldOut
      ? ALL_SOLD_OUT_GUIDE
      : showPartialSoldOutGuide
        ? PARTIAL_SOLD_OUT_GUIDE
        : status === 'recruiting'
          ? '모든 상품의 옵션을 선택해 주세요.'
          : null,
    productSectionGuideEmphasis: allSoldOut || showPartialSoldOutGuide ? 'red' : undefined,
    contentType: buildContentType(status, data),
    contentGuide: buildContentGuide(campaign),
    shippingTracking: buildShippingTracking(fulfillments, contentSubmissions),
    contentSubmission: buildContentSubmission(
      campaign,
      fulfillments,
      contentSubmissions,
      participation?.selectedContentType ?? null,
      isCompleted,
    ),
    selectionNumber:
      APPLICATION_NUMBER_STATUSES.has(status) && participation ? String(participation.id) : null,
    shippingAddress: buildShippingAddress(deliveryAddress, EDITABLE_ADDRESS_STATUSES.has(status)),
    rejected: null,
    penalty,
    primaryCta: applyPenaltyToCta(
      buildPrimaryCta(status, { applyDisabled: isWithdrawnReentry, allSoldOut }),
      penalty !== null,
    ),
    requiredSnsChannels: campaign.snsChannels,
  };
};
